import React, { useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import GenericCard from "@ui-components/Card/GenericCard";
import { CollapsibleSection } from "@ui-components/CollapsibleSection/CollapsibleSection";
import { useAlerts } from "@hooks/useAlerts";
import { RequireTenantType } from "@shared/components/RequireTenantType";
import { useAppSelector } from "@redux/hooks";
import { useAuditLogDetail } from "./hooks/useAuditLogDetail";
import { ActionChip } from "./components/ActionChip";
import { SourceChip } from "./components/SourceChip";
import { AuditLogDiffView } from "./components/AuditLogDiffView";
import { humanizeResourceType } from "./AuditLogsData";
import {
  formatAuditDateAbsolute,
  formatAuditDateRelative,
} from "./utils/dateFormatting";
import type { AuditLog } from "./types";

const AUDIT_LOGS_LIST_PATH = "/support/audit-logs";
const ALLOWED_TENANT_TYPES = ["GLOBAL_ADMIN"];

interface DetailLocationState {
  auditLog?: AuditLog;
}

const AuditLogDetailPageContent: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams<{ id?: string }>();
  const location = useLocation();
  const { alert, Alert } = useAlerts();

  const idNumber = useMemo(() => {
    const raw = params.id;
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) && Number.isInteger(n) && n > 0 ? n : null;
  }, [params.id]);

  // Item passed via `location.state` from the list view (instant render).
  // Only honored when the id matches, to avoid stale renders if the user
  // hand-edits the URL.
  const seedData = useMemo<AuditLog | null>(() => {
    const state = location.state as DetailLocationState | null;
    const seeded = state?.auditLog;
    if (!seeded || idNumber == null) return null;
    return String(seeded.id) === String(idNumber) ? seeded : null;
  }, [location.state, idNumber]);

  // Tenant scope from the global header selection — same semantics as
  // `MainAuditLogs`. Sent as `?tenantId=<n>` so a partner can resolve a
  // detail belonging to one of their customers; backend handles 403 if
  // ownership doesn't match.
  const selectionTenantId = useAppSelector((s) => s.selection.tenantId);
  const tenantScope = selectionTenantId > 0 ? selectionTenantId : null;

  const { data, isFetching, hasFetched, error, errorStatus, refetch } =
    useAuditLogDetail(idNumber, { seedData, tenantId: tenantScope });

  /* ── 404 / 403 handling ───────────────────────────────────────────────
   *
   * We surface these as a modal alert via `useAlerts` (the repo's
   * standard alerting mechanism — no separate toast system exists). The
   * `notifiedRef` guards against re-firing the alert if React Strict
   * Mode double-invokes the effect.
   */
  const notifiedRef = useRef<number | null>(null);
  useEffect(() => {
    if (!hasFetched || !error || errorStatus == null) return;
    if (errorStatus !== 404 && errorStatus !== 403) return;
    if (notifiedRef.current === errorStatus) return;
    notifiedRef.current = errorStatus;

    const title =
      errorStatus === 404
        ? "Audit log entry not found"
        : "You don't have access to this audit log";
    alert({
      title,
      description: error,
      variant: "error",
    }).then(() => {
      navigate(AUDIT_LOGS_LIST_PATH, { replace: true });
    });
  }, [hasFetched, error, errorStatus, alert, navigate]);

  /* ── Invalid id in URL ────────────────────────────────────────────── */

  if (idNumber == null) {
    return (
      <>
        {Alert}
        <div className="p-4">
          <BackLink onClick={() => navigate(AUDIT_LOGS_LIST_PATH)} />
          <InlineMessage
            tone="error"
            title="Invalid audit log id"
            message={`"${params.id ?? ""}" is not a valid id.`}
          />
        </div>
      </>
    );
  }

  /* ── Loading state (no seed) ──────────────────────────────────────── */

  if (!data && isFetching && !hasFetched) {
    return (
      <>
        {Alert}
        <div className="p-4">
          <BackLink onClick={() => navigate(AUDIT_LOGS_LIST_PATH)} />
          <DetailSkeleton />
        </div>
      </>
    );
  }

  /* ── Error state (non-404/403; the modal already covers those) ───── */

  if (!data && error) {
    return (
      <>
        {Alert}
        <div className="p-4">
          <BackLink onClick={() => navigate(AUDIT_LOGS_LIST_PATH)} />
          <InlineMessage
            tone="error"
            title="Failed to load audit log"
            message={error}
            actionLabel="Retry"
            onAction={refetch}
          />
        </div>
      </>
    );
  }

  if (!data) {
    // Defensive fallback (shouldn't be reachable in practice).
    return (
      <>
        {Alert}
        <div className="p-4">
          <BackLink onClick={() => navigate(AUDIT_LOGS_LIST_PATH)} />
          <InlineMessage
            tone="info"
            title="No data"
            message="This audit log entry has no data to display."
          />
        </div>
      </>
    );
  }

  /* ── Happy path ───────────────────────────────────────────────────── */

  const { resourceName, resourceType, parentType } = data;
  const hasParent = parentType != null;
  const metadataKeys = data.metadata ? Object.keys(data.metadata) : [];
  const hasMetadata = metadataKeys.length > 0;

  return (
    <>
      {Alert}
      <div className="flex h-full flex-col overflow-auto p-4">
        <BackLink onClick={() => navigate(AUDIT_LOGS_LIST_PATH)} />

        <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-3">
              {/* Title intentionally does NOT include the entry id —
                  internal numeric ids are noise for end users; the URL
                  itself disambiguates the entry. */}
              <h1 className="text-2xl font-semibold text-semantic-text-primary">
                Audit log details
              </h1>
              <ActionChip action={data.action} size="medium" />
              <SourceChip source={data.source} size="medium" />
            </div>
            <p
              className="mt-1 text-sm text-semantic-text-secondary"
              title={formatAuditDateAbsolute(data.createdAt)}
            >
              {formatAuditDateRelative(data.createdAt)} ·{" "}
              {formatAuditDateAbsolute(data.createdAt)}
            </p>
          </div>
          {isFetching && hasFetched ? <RefreshIndicator /> : null}
        </header>

        {/* Cards intentionally omit numeric ids (entry id, user id,
            product id, resource id, parent id). They are internal
            identifiers; the user only needs the human-readable handles
            (email, product name, resource name). */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <GenericCard
            isCollapsible={false}
            headerContent={<CardTitle>Metadata</CardTitle>}
            bodyContent={
              <DefinitionList
                items={[
                  {
                    label: "Date",
                    value: formatAuditDateAbsolute(data.createdAt),
                  },
                  { label: "User", value: data.userEmail || "—" },
                  { label: "Product", value: data.product?.name ?? "—" },
                  { label: "Source", value: data.source },
                  { label: "Action", value: data.action },
                ]}
              />
            }
          />

          <GenericCard
            isCollapsible={false}
            headerContent={<CardTitle>Resource</CardTitle>}
            bodyContent={
              <DefinitionList
                items={[
                  {
                    label: "Type",
                    value: humanizeResourceType(resourceType),
                  },
                  { label: "Name", value: resourceName ?? "—" },
                  ...(hasParent
                    ? [
                        {
                          label: "Parent type",
                          value: humanizeResourceType(parentType ?? "—"),
                        },
                      ]
                    : []),
                ]}
              />
            }
          />
        </div>

        <div className="mt-4">
          <GenericCard
            isCollapsible={false}
            headerContent={<CardTitle>Changes</CardTitle>}
            bodyContent={<AuditLogDiffView changes={data.changes} />}
          />
        </div>

        {hasMetadata ? (
          <div className="mt-4 rounded-md border border-semantic-border-secondary bg-semantic-bg-primary">
            <CollapsibleSection title="Additional metadata">
              <pre className="max-h-96 overflow-auto whitespace-pre-wrap break-words rounded bg-semantic-bg-secondary p-3 font-mono text-xs text-semantic-text-primary">
                {JSON.stringify(data.metadata, null, 2)}
              </pre>
            </CollapsibleSection>
          </div>
        ) : null}
      </div>
    </>
  );
};

/* ──────────────────────────────────────────────────────────────────────────────
 * Subcomponents
 * ──────────────────────────────────────────────────────────────────────────── */

interface BackLinkProps {
  onClick: () => void;
}

const BackLink: React.FC<BackLinkProps> = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-semantic-text-brand hover:underline"
  >
    <span aria-hidden>←</span>
    <span>Back to audit logs</span>
  </button>
);

const CardTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h2 className="text-base font-semibold text-semantic-text-primary">
    {children}
  </h2>
);

interface DefinitionListItem {
  label: string;
  value: string;
  sub?: string;
}

const DefinitionList: React.FC<{ items: DefinitionListItem[] }> = ({
  items,
}) => (
  <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
    {items.map((item) => (
      <div key={item.label} className="min-w-0">
        <dt className="text-xs font-semibold uppercase tracking-wide text-semantic-text-secondary">
          {item.label}
        </dt>
        <dd className="mt-0.5 break-words text-sm text-semantic-text-primary">
          {item.value}
        </dd>
        {item.sub ? (
          <dd className="text-xs text-semantic-text-tertiary">{item.sub}</dd>
        ) : null}
      </div>
    ))}
  </dl>
);

interface InlineMessageProps {
  tone: "info" | "error";
  title: string;
  message: string;
  actionLabel?: string;
  onAction?: () => void;
}

const InlineMessage: React.FC<InlineMessageProps> = ({
  tone,
  title,
  message,
  actionLabel,
  onAction,
}) => {
  const wrap =
    tone === "error"
      ? "border-semantic-border-danger-primary bg-semantic-bg-danger-secondary"
      : "border-semantic-border-secondary bg-semantic-bg-secondary";
  const text =
    tone === "error" ? "text-semantic-text-danger" : "text-semantic-text-primary";

  return (
    <div
      role="alert"
      className={`flex items-start justify-between gap-3 rounded-md border p-3 ${wrap}`}
    >
      <div className="min-w-0">
        <p className={`text-sm font-medium ${text}`}>{title}</p>
        <p className={`text-xs opacity-80 ${text}`}>{message}</p>
      </div>
      {actionLabel && onAction ? (
        <button
          type="button"
          onClick={onAction}
          className="flex-shrink-0 rounded-md border border-semantic-border-danger-primary px-3 py-1 text-xs font-medium text-semantic-text-danger hover:bg-semantic-bg-danger-secondary"
        >
          {actionLabel}
        </button>
      ) : null}
    </div>
  );
};

const RefreshIndicator: React.FC = () => (
  <div className="inline-flex items-center gap-2 rounded-full border border-semantic-border-secondary bg-semantic-bg-primary px-3 py-1">
    <span className="block h-3 w-3 animate-spin rounded-full border-2 border-semantic-border-secondary border-t-semantic-text-brand" />
    <span className="text-xs text-semantic-text-secondary">Refreshing…</span>
  </div>
);

const DetailSkeleton: React.FC = () => (
  <div className="animate-pulse space-y-4">
    <div className="h-8 w-1/3 rounded bg-semantic-bg-secondary" />
    <div className="h-4 w-1/4 rounded bg-semantic-bg-secondary" />
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <div className="h-48 rounded-md border border-semantic-border-secondary bg-semantic-bg-secondary" />
      <div className="h-48 rounded-md border border-semantic-border-secondary bg-semantic-bg-secondary" />
    </div>
    <div className="h-64 rounded-md border border-semantic-border-secondary bg-semantic-bg-secondary" />
  </div>
);

/**
 * Public entry. Wraps the detail page in `RequireTenantType` so the
 * inner content (and its API hooks) only mount when the user is a
 * GLOBAL_ADMIN. Direct URL access by other tenant types redirects.
 */
const AuditLogDetailPage: React.FC = () => (
  <RequireTenantType allowedTypes={ALLOWED_TENANT_TYPES}>
    <AuditLogDetailPageContent />
  </RequireTenantType>
);

export default AuditLogDetailPage;
